import React, { useEffect, useState, useContext } from "react";
import Twig from "utils/Twig";
import MonacoEditor from '@monaco-editor/react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LaunchIcon from '@mui/icons-material/Launch';
import { Editor as TinymceEditor } from '@tinymce/tinymce-react';
import { getReportsByID, updateTemplateReport, executeReport } from 'utils/Reports';
import { useSnackbar } from 'notistack';
import { GlobalContext } from 'GlobalContext';
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import SaveIcon from '@mui/icons-material/Save';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CodeIcon from '@mui/icons-material/Code';
import ArticleIcon from '@mui/icons-material/Article';
import PreviewIcon from '@mui/icons-material/Preview';

interface TemplateReportProps {
    report_id: number;
}


const getReport = async (id: number) => {
    return await getReportsByID(id);
};

function getKeys(obj: Record<string, any>, prefix: string = ''): string[] {
    // let keys: string[] = [];
    let keys: Set<string> = new Set();
    function addKeyVariants(keyPath: string) {
        const parts = keyPath.split('.');
        let partialKey = '';
        for (let i = 0; i < parts.length; i++) {
            partialKey = i === 0 ? parts[i] : `${partialKey}.${parts[i]}`;
            keys.add(partialKey);
        }
    }
    function traverse(obj: Record<string, any>, prefix: string = '') {
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const newKey = prefix ? `${prefix}.${key}` : key;
                addKeyVariants(newKey);
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                    traverse(obj[key], newKey);
                }
            }
        }
    }
    // for (const key in obj) {
    //     if (Object.prototype.hasOwnProperty.call(obj, key)) {
    //         const newKey = prefix ? `${prefix}.${key}` : key;
    //         keys.push(newKey);
    //         if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
    //             keys = keys.concat(getKeys(obj[key], newKey));
    //         }
    //     }
    // }

    traverse(obj);
    return Array.from(keys);
    // return keys;
}

const TemplateReport: React.FC<TemplateReportProps> = ({ report_id }) => {

    const { themeMode } = useContext(GlobalContext)

    const { enqueueSnackbar } = useSnackbar();

    const [report, setReport] = useState<Report | null>(null);

    const [jsonData, setJsonData] = useState(``);
    const [twigTemplate, setTwigTemplate] = useState(``);
    const [output, setOutput] = useState(``);
    const [, setSuggestions] = useState<string[]>([]);
    const [specialChars, setSpecialChars] = useState<any[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const newTabRef = useRef<Window | null>(null);

    const TabsType = Object.freeze({
        DATA: 0,
        TEMPLATE: 1,
        EDITOR: 2,
        OUTPUT: 3
    });

    const cargarData = async () => {
        const reporte = await getReport(report_id)
        setReport(reporte)
        // setJsonData()
        setTwigTemplate(reporte.template)
    }



    useEffect(() => {
        cargarData()
    }, [])


    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
    };

    useEffect(() => {
        renderTwig();
    }, [twigTemplate, jsonData]);

    const editorInit = {
        skin: themeMode === "light" ? "oxide" : "oxide-dark",
        content_css: themeMode === "light" ? "default" : "dark",
        plugins: [
            // Core editing features
            'anchor', 'autolink', 'charmap', 'codesample', 'emoticons', 'image', 'link', 'lists', 'media', 'searchreplace', 'table', 'visualblocks', 'wordcount',
            // Your account includes a free trial of TinyMCE premium features
            // Try the most popular premium features until Mar 24, 2025:
            'checklist', 'mediaembed', 'casechange', 'export', 'formatpainter', 'pageembed', 'a11ychecker', 'tinymcespellchecker', 'permanentpen', 'powerpaste', 'advtable', 'advcode', 'editimage', 'advtemplate', 'ai', 'mentions', 'tinycomments', 'tableofcontents', 'footnotes', 'mergetags', 'autocorrect', 'typography', 'inlinecss', 'markdown', 'importword', 'exportword', 'exportpdf',
        ],
        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
        tinycomments_mode: 'embedded',
        tinycomments_author: 'Author name',
        mergetags_list: [
            // { value: 'First.Name', title: 'First Name' },
            // { value: 'Email', title: 'Email' },
        ],
        ai_request: (request: any, respondWith: any) => respondWith.string(() => Promise.reject('See docs to implement AI Assistant')),
        setup: (editor: any) => {
            const onAction = (autocompleteApi: any, rng: any, value: any) => {
                editor.selection.setRng(rng);
                editor.insertContent(value);
                autocompleteApi.hide();
            };

            const getMatchedChars = (pattern: any) => {
                return specialChars.filter((char) => char.text.indexOf(pattern) !== -1);
            };

            editor.ui.registry.addAutocompleter('specialchars', {
                trigger: '{{',
                minChars: 1,
                columns: 1,
                highlightOn: ['char_name'],
                onAction: onAction,
                fetch: (pattern: any) => {
                    return new Promise((resolve) => {
                        const results = getMatchedChars(pattern).map((char) => ({
                            type: 'cardmenuitem', // cardmenuitem | autocompleteitem
                            value: char.value,
                            label: char.text,
                            // text: char.text,
                            // icon: char.value
                            items: [
                                {
                                    type: 'cardcontainer',
                                    direction: 'vertical',
                                    items: [
                                        {
                                            type: 'cardtext',
                                            text: char.text,
                                            name: 'char_name'
                                        },
                                        // {
                                        //     type: 'cardtext',
                                        //     text: char.value
                                        // }
                                    ]
                                }
                            ]
                        }));
                        resolve(results);
                    });
                }
            });
        }
    }

    useEffect(() => {
        try {
            const parsedData = JSON.parse(jsonData);
            const keysList = getKeys(parsedData);
            setSuggestions(keysList);
            const specialchars: { text: string, value: string }[] = [
                // { text: 'exclamation mark', value: '!' },
            ];
            keysList.map((key) => {
                specialchars.push({ text: key, value: `{{${key}` });
            });
            setSpecialChars(specialchars)
        } catch (error) {

        }
    }, [jsonData]);

    useEffect(() => {
        const handleTabClose = () => {
            if (newTabRef.current && newTabRef.current.closed) {
                setSelectedTab(TabsType.OUTPUT);
                newTabRef.current = null;
            }
        };

        window.addEventListener('focus', handleTabClose);
        return () => {
            window.removeEventListener('focus', handleTabClose);
        };
    }, []);


    const renderTwig = () => {
        try {
            let data = {};
            data = JSON.parse(jsonData);
            let template = Twig.twig({
                data: twigTemplate
            });
            let output = template.render(data);
            setOutput(output);
            if (newTabRef.current) {
                newTabRef.current.document.body.innerHTML = output;
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleEditorChangeJsonData = (value: string | undefined) => {
        setJsonData(value || '');
    };

    const handleEditorChangeTemplate = (value: string | undefined) => {
        setTwigTemplate(value || '');
    };

    const openNewTab = () => {
        newTabRef.current = window.open("", "Reporte", "toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,");
        if (newTabRef.current) {
            newTabRef.current.document.write(`
                <html>
                    <head>
                        <style>
                            body {
                                background-color: #121212;
                                color: #ffffff;
                            }
                        </style>
                    </head>
                    <body>${output}</body>
                </html>
            `);
            setSelectedTab(TabsType.DATA); // Ocultar la pestaña "Output"
        }
    };

    const handleSaveTemplate = async () => {
        if (report) {
            try {
                enqueueSnackbar("Guardando", { variant: 'info', autoHideDuration: 1000 });
                await updateTemplateReport({ template: twigTemplate, report_id: report.id });
                enqueueSnackbar("Guardado", { variant: 'success', autoHideDuration: 1000 });
            } catch (error) {
                console.error("Error al guardar el template:", error);
                enqueueSnackbar(`Error ${error}`, { variant: 'error' });
            }
        }
    };

    const handleSelectJsonDataOrigin = async () => {
        try {
            throw new Error("Not Implemented");
        } catch (error) {
            enqueueSnackbar(`Error ${error}`, { variant: 'error' });
        }
    }

    const handleFetchJsonData = async () => {
        if (report) {
            try {
                const response = await executeReport({ report_id: report.id, dry_run: true });
                setJsonData(JSON.stringify(response, null, 2));
                enqueueSnackbar("Datos obtenidos exitosamente", { variant: 'success' });
            } catch (error) {
                console.error("Error al obtener los datos:", error);
                enqueueSnackbar(`Error al obtener los datos ${error}`, { variant: 'error' });
            }
        }
    };

    return (
        <div style={{ height: '80vh' }}>
            <h1>{report?.name}</h1>


            <Tabs value={selectedTab} onChange={handleTabChange}>
                <Tab label="JSON Data" icon={<DataObjectIcon />} />
                <Tab label="Template" icon={<CodeIcon />} />
                <Tab label="Editor" icon={<ArticleIcon />} />
                {newTabRef.current === null && <Tab label="Output" icon={<PreviewIcon />} />} {/* Ocultar la pestaña "Output" */}
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveTemplate}>
                    Guardar
                </Button>

            </Tabs>

            {selectedTab === TabsType.DATA && (
                <div style={{ height: '80vh' }}>

                    <Stack
                        spacing={{ xs: 1, sm: 2 }}
                        sx={{ flexWrap: 'wrap' }}
                        direction="row">
                        <Button variant="contained" onClick={handleSelectJsonDataOrigin}>Seleccionar orgien de datos</Button>
                        <Button variant="contained" onClick={handleFetchJsonData}>Obtener Data Ejemplo</Button>
                    </Stack>

                    <MonacoEditor
                        language="json"
                        value={jsonData}
                        theme={`vs-${themeMode}`}
                        onChange={handleEditorChangeJsonData}
                    />
                </div>
            )}

            {selectedTab === TabsType.TEMPLATE && (
                <div style={{ height: '80vh' }}>
                    <MonacoEditor
                        language="twig"
                        value={twigTemplate}
                        theme={`vs-${themeMode}`}
                        onChange={handleEditorChangeTemplate}
                    />
                </div>
            )}

            {selectedTab === TabsType.EDITOR && (
                <div style={{ height: '80vh' }}>

                    <TinymceEditor
                        apiKey='36bd5iplvi07lsp9jbt8qmoe8uk5un5aviyv2abgvst51c9f'
                        init={editorInit}
                        value={twigTemplate}
                        onEditorChange={(content: any) => setTwigTemplate(content)}
                    />
                </div>
            )}

            {selectedTab === TabsType.OUTPUT && (
                <div>
                    <Button
                        startIcon={<LaunchIcon />}
                        variant="contained" onClick={openNewTab}>Open in New Tab</Button>
                    <div dangerouslySetInnerHTML={{ __html: output }}></div>
                </div>
            )}
        </div>
    );
}

export { TemplateReport };
